"use client";

// Application Process — shared redesign module (Figma "Partnership Steps",
// nodes 8241:21222 partners, 6702:29546 hosted-capital, 8196:81219 products).
// Dark three-column step panel with blue accent glow by default; used on
// /partners, /hosted-capital, /products, and any route sharing #application-process.
//
// Autoplay + top-rail progress reuse the Company History timer pattern (Figma
// 8196:24698 gradient tag): ~5s per step, starts when scrolled into view.

import { motion, useMotionValue } from "framer-motion";
import * as React from "react";

import { GrainOverlay } from "@/components/ui/grain-overlay";
import { Link } from "@/components/ui/link";
import { Section } from "@/components/ui/section";
import { cn } from "@/lib/utils";

export interface ApplicationProcessStep {
  tag: string;
  title: string;
  description: string;
}

interface ApplicationProcessProps {
  id?: string;
  title: string;
  description?: string;
  ctaLabel?: string;
  ctaHref?: string;
  /** Use ExternalLink (app attribution) instead of localized NextLink. */
  ctaExternal?: boolean;
  steps: ApplicationProcessStep[];
  /** Bottom glow + dot-grid tint; default blue matches hosted-capital / partners. */
  accent?: "blue" | "purple";
  /** Hide the promo-row CTA below `lg` (SBL mobile Figma 9727:35550). */
  promoCtaDesktopOnly?: boolean;
  /** Optional class names for the dark inner panel (e.g. SBL mobile vertical rhythm). */
  innerPanelClassName?: string;
  /** Gap between title row and step grid (default gap-10 lg:gap-14). */
  stepsIntroGapClassName?: string;
  /** Use the large promo title row without requiring description/CTA (partners Figma 6560:29279). */
  promoTitleRow?: boolean;
  className?: string;
}

const ACCENT_GLOW: Record<
  NonNullable<ApplicationProcessProps["accent"]>,
  string
> = {
  purple:
    "radial-gradient(70% 100% at 50% 100%, oklch(0.45 0.27 295 / 0.65) 0%, transparent 70%)",
  blue: "radial-gradient(70% 100% at 50% 100%, oklch(0.4654 0.3042 264.2 / 0.65) 0%, transparent 70%)",
};

const ACCENT_GRID: Record<
  NonNullable<ApplicationProcessProps["accent"]>,
  string
> = {
  purple: "radial-gradient(oklch(0.7 0.02 280 / 0.5) 0.5px, transparent 0.5px)",
  blue: "radial-gradient(oklch(0.7 0.04 264 / 0.45) 0.5px, transparent 0.5px)",
};

const STEP_TAG_BASE =
  "text-ds-label-md inline-flex h-5 w-fit items-center rounded-ds-s px-2 py-1 font-medium leading-none transition-colors duration-500";

// ~5s per step (issue WEB-737); Company History uses 4500ms on the same timer.
const AUTOPLAY_MS = 5000;

export function ApplicationProcess({
  id,
  title,
  description,
  ctaLabel,
  ctaHref = "#contact-us",
  ctaExternal = false,
  steps,
  accent = "blue",
  promoCtaDesktopOnly = false,
  innerPanelClassName,
  stepsIntroGapClassName = "gap-10 lg:gap-14",
  promoTitleRow = false,
  className,
}: ApplicationProcessProps) {
  const hasPromoHeader = Boolean(description || ctaLabel || promoTitleRow);
  const sectionRef = React.useRef<HTMLElement>(null);
  const inViewRef = React.useRef(false);
  const [activeIndex, setActiveIndex] = React.useState(0);
  const progress = useMotionValue(0);
  const reducedMotion = usePrefersReducedMotion();

  React.useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        inViewRef.current = entry?.isIntersecting ?? false;
      },
      { threshold: 0.35 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    if (reducedMotion || steps.length <= 0) return;

    let elapsed = 0;
    let lastFrame = performance.now();
    let rafId = 0;

    const tick = (now: number) => {
      const dt = Math.min(now - lastFrame, 200);
      lastFrame = now;

      if (!document.hidden && inViewRef.current) {
        elapsed += dt;
        if (elapsed >= AUTOPLAY_MS) {
          elapsed = 0;
          setActiveIndex((current) => (current + 1) % steps.length);
          progress.set(0);
        } else {
          progress.set(elapsed / AUTOPLAY_MS);
        }
      }

      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [progress, reducedMotion, steps.length]);

  return (
    <Section
      ref={sectionRef}
      id={id}
      data-testid="application-process-section"
      className={cn("bg-grey-50 px-6 py-8 md:px-8 lg:p-8", className)}
    >
      <div
        className={cn(
          "bg-grey-950 relative isolate mx-auto flex w-full max-w-[1376px] flex-col overflow-hidden rounded-2xl px-6 py-10 md:min-h-[552px] md:px-12 md:py-16 lg:px-12",
          innerPanelClassName
        )}
      >
        <GrainOverlay
          baseFrequency={0.85}
          numOctaves={2}
          contrast={2.4}
          opacity={0.45}
          blendMode="overlay"
        />

        <span
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 -z-10 h-[55%]"
          style={{ background: ACCENT_GLOW[accent] }}
        />
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-10 opacity-[0.18]"
          style={{
            backgroundImage: ACCENT_GRID[accent],
            backgroundSize: "24px 24px",
          }}
        />

        <div className={cn("relative flex flex-col", stepsIntroGapClassName)}>
          {hasPromoHeader ? (
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div className="flex max-w-[798px] flex-col gap-4">
                <h2 className="text-grey-0 md:text-ds-display-sm text-[24px] leading-[1.2] tracking-[-0.04em] text-balance md:leading-[1.3]">
                  {title}
                </h2>
                {description ? (
                  <p className="text-ds-body-lg text-grey-500 leading-[1.3] tracking-[-0.02em]">
                    {description}
                  </p>
                ) : null}
              </div>
              {ctaLabel ? (
                <Link
                  href={ctaHref}
                  external={ctaExternal}
                  className={cn(
                    "bg-pulse-700 text-grey-0 text-ds-label-md inline-flex h-8 w-fit shrink-0 items-center justify-center self-start rounded-full px-4 font-medium",
                    promoCtaDesktopOnly && "hidden lg:inline-flex"
                  )}
                >
                  {ctaLabel}
                </Link>
              ) : null}
            </div>
          ) : (
            <h2 className="text-ds-heading-h3 md:text-ds-display-sm text-grey-0 max-w-[1170px] text-balance md:leading-[1.3]">
              {title}
            </h2>
          )}

          <div
            className={cn(
              "relative grid gap-8",
              hasPromoHeader ? "lg:grid-cols-3" : "md:grid-cols-3"
            )}
          >
            {steps.map((step, index) => {
              const isActive = index === activeIndex;

              return (
                <article
                  key={step.tag}
                  aria-current={isActive ? "step" : undefined}
                  className="border-grey-800 relative flex flex-col gap-4 border-t pt-10"
                >
                  {isActive ? (
                    <motion.span
                      aria-hidden
                      className="bg-grey-0 absolute top-0 left-0 h-px w-full origin-left will-change-transform"
                      style={{ scaleX: progress }}
                    />
                  ) : null}
                  <StepTag label={step.tag} active={isActive} />
                  <h3 className="text-ds-heading-h5 text-grey-0 leading-[1.3]">
                    {step.title}
                  </h3>
                  <p className="text-ds-body-md text-grey-300 leading-[1.5]">
                    {step.description}
                  </p>
                </article>
              );
            })}
          </div>
        </div>
      </div>
    </Section>
  );
}

function StepTag({ label, active }: { label: string; active: boolean }) {
  if (!active) {
    return (
      <span className={cn(STEP_TAG_BASE, "bg-grey-800 text-grey-0")}>
        {label}
      </span>
    );
  }

  return (
    <span className={cn(STEP_TAG_BASE, "bg-ds-gradient-secondary text-grey-0")}>
      {label}
    </span>
  );
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = React.useState(false);

  React.useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  return reduced;
}
